import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import DOMPurify from 'dompurify'
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Button,
  Avatar,
  Divider,
  LinearProgress,
  Collapse,
  Tooltip,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material'
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Send as SendIcon,
  CheckCircle as DeliveredIcon,
  Schedule as QueuedIcon,
  Error as FailedIcon,
  Email as EmailIcon,
  Add as AddIcon,
  AttachFile as AttachFileIcon,
  Close as CloseIcon,
  InsertDriveFile as FileIcon,
  Download as DownloadIcon,
  Minimize as MinimizeIcon,
  OpenInFull as MaximizeIcon,
  AutoAwesome as AIIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  Reply as ReplyIcon,
  ArrowBack as BackIcon,
  OpenInNew as OpenInNewIcon,
  Archive as ArchiveIcon,
  Delete as DeleteIcon,
  MarkEmailRead as MarkReadIcon,
  MarkEmailUnread as MarkUnreadIcon,
  Inbox as InboxIcon,
  DeleteOutline as TrashIcon,
} from '@mui/icons-material'
import { Email, emailsApi, Client, clientsApi, attachmentsApi, Attachment } from '../services/api'
import { colors } from '../theme'
import { useNotification } from '../hooks/useNotification'
import PageHelpButton from '../components/PageHelpButton'
import { useWebSocket } from '../hooks/useWebSocket'
import ReactQuill from 'react-quill-new'
import 'react-quill-new/dist/quill.snow.css'
import LoadingSpinner from '../components/LoadingSpinner'
import { useClientSuccessMap } from '../hooks/useClientSuccessMap'
import ClientPicker from '../components/common/ClientPicker'
import { useAtom } from 'jotai'
import { userAtom } from '../state/auth'
import { isAdminOrManager } from '../utils/roles'
import { emailPreview } from '../utils/emailPreview'
import { parseRecipients } from '../utils/emailRecipients'
import { quillHtmlToEmailHtml } from '../utils/emailBody'

// Register a custom clipboard module that preserves rich HTML on paste
// In Quill 2.x, onCapturePaste(e) handles the DOM event and extracts HTML,
// then calls onPaste(range, { html, text }). We override onPaste to sanitize
// the HTML and insert it directly instead of letting Quill's convert() strip tables/formatting.
const Quill = ReactQuill.Quill
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const BaseClipboard = Quill.import('modules/clipboard') as any

const PASTE_SANITIZE_CONFIG = {
  ALLOWED_TAGS: [
    'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'a',
    'h1', 'h2', 'h3', 'h4',
    'ul', 'ol', 'li',
    'table', 'thead', 'tbody', 'tr', 'th', 'td', 'caption', 'colgroup', 'col',
    'blockquote', 'pre', 'code',
    'div', 'span', 'img', 'hr',
  ],
  ALLOWED_ATTR: [
    'href', 'target', 'rel', 'style', 'class',
    'colspan', 'rowspan', 'align', 'valign', 'width', 'height',
    'src', 'alt',
    'border', 'cellpadding', 'cellspacing',
  ],
  ALLOW_DATA_ATTR: false,
}

class RichPasteClipboard extends BaseClipboard {
  // Override onPaste which receives (range, { html, text }) in Quill 2.x
  onPaste(range: { index: number; length: number }, { html, text }: { html?: string; text?: string }) {
    if (html) {
      const sanitized = DOMPurify.sanitize(html, PASTE_SANITIZE_CONFIG)
      // Delete any selected content first
      if (range.length > 0) {
        this.quill.deleteText(range.index, range.length, 'silent')
      }
      // Insert sanitized HTML directly, bypassing Quill's convert() which strips tables
      this.quill.clipboard.dangerouslyPasteHTML(range.index, sanitized, 'user')
    } else {
      // Fall back to default behavior for plain text paste
      super.onPaste(range, { html, text })
    }
  }
}
Quill.register('modules/clipboard', RichPasteClipboard, true)

// Check if Quill editor content is effectively empty
const isQuillEmpty = (html: string) => !html || html.replace(/<[^>]*>/g, '').trim() === ''

// Status icons for email status display
const statusIcons: Record<string, React.ReactNode> = {
  queued: <QueuedIcon fontSize="small" sx={{ color: 'text.secondary' }} />,
  sent: <QueuedIcon fontSize="small" sx={{ color: 'text.secondary' }} />,
  delivered: <DeliveredIcon fontSize="small" sx={{ color: 'success.main' }} />,
  failed: <FailedIcon fontSize="small" sx={{ color: 'error.main' }} />,
  received: null,
}

interface EmailThread {
  id: string // Using clientId as thread id for now
  clientId: string
  clientName: string
  clientEmail: string
  subject: string
  lastEmail: Email
  emails: Email[]
  emailCount: number
  hasUnread: boolean
  starred: boolean
}

export default function Emails() {
  const navigate = useNavigate()
  const [user] = useAtom(userAtom)
  const [searchParams] = useSearchParams()
  const { showSuccess, showError, showUndoNotification } = useNotification()
  const { load: loadCsData, getClientSuccessName } = useClientSuccessMap()
  const [allEmails, setAllEmails] = useState<Email[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '')
  // Carried alongside ?search= purely so the inbox can point back at the client
  // it was opened from. The filtering itself still runs off the search term.
  const filterClientId = searchParams.get('clientId')
  const [selectedThread, setSelectedThread] = useState<EmailThread | null>(null)
  const [expandedEmails, setExpandedEmails] = useState<Set<string>>(new Set())
  const [selectedThreadIds, setSelectedThreadIds] = useState<Set<string>>(new Set())
  const [activeFilter, setActiveFilter] = useState<'all' | 'unread' | 'starred' | 'archived' | 'trash'>('all')
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [exportingPdf, setExportingPdf] = useState(false)
  const emailsEndRef = useRef<HTMLDivElement>(null)

  // Reply state
  const [replyOpen, setReplyOpen] = useState(false)
  const [replySubject, setReplySubject] = useState('')
  const [replyBody, setReplyBody] = useState('')
  const [replySending, setReplySending] = useState(false)
  const [replyAttachments, setReplyAttachments] = useState<Attachment[]>([])
  const [uploadingReplyAttachment, setUploadingReplyAttachment] = useState(false)
  const replyFileInputRef = useRef<HTMLInputElement>(null)

  // Reply AI assist state
  const [replyAiPrompt, setReplyAiPrompt] = useState('')
  const [replyGeneratingAI, setReplyGeneratingAI] = useState(false)
  const [showReplyAIInput, setShowReplyAIInput] = useState(false)

  // Compose dialog state
  const [composeOpen, setComposeOpen] = useState(false)
  const [clients, setClients] = useState<Client[]>([])
  const [loadingClients, setLoadingClients] = useState(false)
  const [composeClient, setComposeClient] = useState<Client | null>(null)
  const [composeSubject, setComposeSubject] = useState('')
  const [composeBody, setComposeBody] = useState('')
  const [composeSending, setComposeSending] = useState(false)
  const [manualEmailMode, setManualEmailMode] = useState(false)
  const [manualEmail, setManualEmail] = useState('')

  // Attachment state for compose dialog
  const [composeAttachments, setComposeAttachments] = useState<Attachment[]>([])
  const [uploadingAttachment, setUploadingAttachment] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Compose drawer state
  const [composeMinimized, setComposeMinimized] = useState(false)

  // AI assist state
  const [aiPrompt, setAiPrompt] = useState('')
  const [generatingAI, setGeneratingAI] = useState(false)
  const [showAIInput, setShowAIInput] = useState(false)

  // Quill editor modules configuration for email compose
  const quillModules = useMemo(
    () => ({
      toolbar: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ align: [] }],
        ['link'],
        ['clean'],
      ],
    }),
    []
  )

  // WebSocket handler for real-time email updates
  const handleEmailEvent = useCallback(
    (message: { action: string; data: unknown }) => {
      const emailData = message.data as Email

      // email_arrived is the same inbound email sent to every staff member, not only the
      // client's team (MWA-390); the team also gets email_received, and the id check below
      // keeps the two from listing it twice.
      if (message.action === 'email_sent' || message.action === 'email_received' || message.action === 'email_arrived') {
        setAllEmails((prev) => {
          if (prev.some((e) => e.id === emailData.id)) return prev
          return [...prev, emailData]
        })
      } else if (message.action === 'email_status_update') {
        setAllEmails((prev) =>
          prev.map((e) =>
            e.id === emailData.id ? { ...e, status: emailData.status } : e
          )
        )
      } else if (message.action === 'emails_bulk_updated') {
        const { emailIds, updates } = message.data as { emailIds: string[]; updates: Partial<Email> }
        setAllEmails((prev) =>
          prev.map((e) => emailIds.includes(e.id) ? { ...e, ...updates } : e)
        )
      }
    },
    []
  )

  // `send` is used by handleSelectThread to emit `mark_client_read` and clear
  // the per-client unread email badge on the pipeline card.
  const { send: wsSend } = useWebSocket({
    email_sent: handleEmailEvent,
    email_received: handleEmailEvent,
    email_arrived: handleEmailEvent,
    email_status_update: handleEmailEvent,
    emails_bulk_updated: handleEmailEvent,
    // MWA-390 — catch up on anything missed while the socket was down.
    ws_reconnected: () => {
      loadEmails()
    },
  })

  useEffect(() => {
    loadEmails()
    loadCsData()
  }, [])

  const loadClients = async () => {
    try {
      setLoadingClients(true)
      const data = await clientsApi.list({ all: true })
      setClients(data.filter((c) => c.email))
    } catch (err) {
      console.error('Error loading clients:', err)
      showError('Failed to load clients')
    } finally {
      setLoadingClients(false)
    }
  }

  const isManager = isAdminOrManager(user) || user?.role === 'client_success'

  // clientIds the logged-in staff member is assigned to (coach / clientSuccess /
  // assignedTo). Drives the "My Clients" group on the compose picker.
  const assignedClientIds = useMemo(() => {
    const email = user?.email
    if (!email) return new Set<string>()
    return new Set(
      clients
        .filter((c) => c.coach === email || c.clientSuccess === email || c.assignedTo === email)
        .map((c) => c.id)
    )
  }, [clients, user?.email])

  // Only clients with an address, since that is what the email goes to. Sorted
  // mine-first because the picker's grouping relies on same-group options being
  // adjacent.
  const composeClientOptions = useMemo(() => {
    return [...clients]
      .filter((c) => c.email)
      .sort((a, b) => {
        if (isManager) {
          const aMine = assignedClientIds.has(a.id) ? 0 : 1
          const bMine = assignedClientIds.has(b.id) ? 0 : 1
          if (aMine !== bMine) return aMine - bMine
        }
        return (a.name || a.email || '').localeCompare(b.name || b.email || '')
      })
  }, [clients, isManager, assignedClientIds])

  const handleOpenCompose = () => {
    setComposeOpen(true)
    if (clients.length === 0) {
      loadClients()
    }
  }

  const handleCloseCompose = () => {
    setComposeOpen(false)
    setComposeClient(null)
    setComposeSubject('')
    setComposeBody('')
    setComposeAttachments([])
    setComposeMinimized(false)
    setAiPrompt('')
    setShowAIInput(false)
    setManualEmailMode(false)
    setManualEmail('')
  }

  const handleAIGenerate = async () => {
    if (!aiPrompt.trim()) return

    try {
      setGeneratingAI(true)
      const result = await emailsApi.generateEmail(
        composeClient?.id || null,
        aiPrompt.trim(),
        {
          existingDraft: isQuillEmpty(composeBody) ? undefined : composeBody,
          recipientName: !composeClient && manualEmail ? manualEmail.split('@')[0] : undefined,
        }
      )
      setComposeSubject(result.subject)
      setComposeBody(result.body)
      setAiPrompt('')
      setShowAIInput(false)
      showSuccess(`Email generated with ${result.tone} tone`)
    } catch (err) {
      console.error('Error generating email:', err)
      showError(err instanceof Error ? err.message : 'Failed to generate email')
    } finally {
      setGeneratingAI(false)
    }
  }

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files || files.length === 0 || !composeClient) return

    for (const file of Array.from(files)) {
      try {
        setUploadingAttachment(true)
        const attachment = await attachmentsApi.upload(
          file,
          'client',
          composeClient.id,
          `Email attachment: ${file.name}`,
          false
        )
        setComposeAttachments((prev) => [...prev, attachment])
      } catch (err) {
        console.error('Error uploading attachment:', err)
        showError(`Failed to upload ${file.name}`)
      } finally {
        setUploadingAttachment(false)
      }
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleRemoveAttachment = (attachmentId: string) => {
    setComposeAttachments((prev) => prev.filter((a) => a.id !== attachmentId))
  }

  const handleDownloadAttachment = async (attachmentId: string) => {
    try {
      await attachmentsApi.download(attachmentId)
    } catch (err) {
      console.error('Error downloading attachment:', err)
      showError('Failed to download attachment')
    }
  }

  const handleExportPdf = async () => {
    if (!selectedThread) return
    setExportingPdf(true)
    try {
      const html2pdf = (await import('html2pdf.js')).default

      const emailsHtml = selectedThread.emails
        .filter((e) => !e.isArchived && !e.isDeleted)
        .map((email, index) => {
          const isOutbound = email.direction === 'outbound'
          const from = isOutbound
            ? email.authorName || email.fromEmail || 'Staff'
            : email.clientName || email.fromEmail || email.email
          const to = isOutbound
            ? email.toEmails?.join(', ') || email.clientName || email.toEmail || email.email
            : email.authorName || email.toEmail || 'Staff'
          const date = new Date(email.createdAt).toLocaleString()
          const body = email.htmlBody || email.textBody || '(No content)'
          const separator =
            index < selectedThread.emails.length - 1
              ? '<hr style="border:none;border-top:1px solid #e0e0e0;margin:16px 0;" />'
              : ''

          return `
            <div style="margin-bottom:8px;">
              <div style="font-size:11px;font-weight:bold;color:${isOutbound ? '#1565c0' : '#2e7d32'};margin-bottom:4px;">
                ${isOutbound ? 'SENT' : 'RECEIVED'}
              </div>
              <div style="font-size:12px;color:#333;margin-bottom:2px;"><b>From:</b> ${from}</div>
              <div style="font-size:12px;color:#333;margin-bottom:2px;"><b>To:</b> ${to}</div>
              <div style="font-size:12px;color:#333;margin-bottom:2px;"><b>Date:</b> ${date}</div>
              ${email.subject ? `<div style="font-size:12px;color:#333;margin-bottom:8px;"><b>Subject:</b> ${email.subject}</div>` : ''}
              <div style="font-size:12px;color:#333;line-height:1.5;">${body}</div>
            </div>
            ${separator}
          `
        })
        .join('')

      const container = document.createElement('div')
      container.innerHTML = `
        <div style="font-family:Arial,sans-serif;padding:20px;">
          <h2 style="margin:0 0 4px 0;font-size:18px;">Email Thread — ${selectedThread.clientName}</h2>
          <div style="font-size:10px;color:#666;margin-bottom:12px;">
            Exported on ${new Date().toLocaleString()}
          </div>
          <hr style="border:none;border-top:1px solid #ccc;margin-bottom:16px;" />
          ${emailsHtml}
        </div>
      `

      const sanitizedName = selectedThread.clientName
        .replace(/[^a-zA-Z0-9 ]/g, '')
        .replace(/\s+/g, '-')
      const fileName = `Email-Thread-${sanitizedName}-${new Date().toISOString().split('T')[0]}.pdf`

      await html2pdf()
        .set({
          margin: [10, 10, 10, 10],
          filename: fileName,
          html2canvas: { scale: 2, useCORS: true },
          jsPDF: { unit: 'mm', format: 'letter', orientation: 'portrait' },
          pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
        })
        .from(container)
        .save()

      showSuccess('Email thread PDF downloaded')
    } catch (err) {
      console.error('Error exporting email thread PDF:', err)
      showError('Failed to export email thread as PDF')
    } finally {
      setExportingPdf(false)
    }
  }

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const handleComposeSend = async () => {
    if (!composeSubject.trim() || isQuillEmpty(composeBody)) return

    if (manualEmailMode) {
      // Sending to manual email address
      const parsedRecipients = parseRecipients(manualEmail)
      if (!parsedRecipients.ok) {
        showError(parsedRecipients.error)
        return
      }

      try {
        setComposeSending(true)
        const attachmentIds = composeAttachments.map((a) => a.id)
        await emailsApi.sendToEmail(
          parsedRecipients.recipients.join(', '),
          composeSubject.trim(),
          quillHtmlToEmailHtml(composeBody.trim()),
          attachmentIds.length > 0 ? attachmentIds : undefined
        )
        showSuccess('Email sent')
        handleCloseCompose()
        loadEmails()
      } catch (err) {
        console.error('Error sending email:', err)
        showError(err instanceof Error ? err.message : 'Failed to send email')
      } finally {
        setComposeSending(false)
      }
    } else {
      // Sending to existing client
      if (!composeClient) {
        showError('Please select a client')
        return
      }

      if (!composeClient.email) {
        showError('Selected client does not have an email address')
        return
      }

      try {
        setComposeSending(true)
        const attachmentIds = composeAttachments.map((a) => a.id)
        await emailsApi.send(
          composeClient.id,
          composeSubject.trim(),
          quillHtmlToEmailHtml(composeBody.trim()),
          attachmentIds.length > 0 ? attachmentIds : undefined
        )
        showSuccess('Email sent')
        handleCloseCompose()
        loadEmails()
      } catch (err) {
        console.error('Error sending email:', err)
        showError(err instanceof Error ? err.message : 'Failed to send email')
      } finally {
        setComposeSending(false)
      }
    }
  }

  const handleSelectThread = async (thread: EmailThread) => {
    setSelectedThread(thread)
    // Mark unread inbound emails in this thread as read
    const unreadEmails = thread.emails.filter((e) => e.direction === 'inbound' && !e.isRead)
    if (unreadEmails.length > 0) {
      // Optimistic update: mark thread as read locally
      thread.hasUnread = false
      unreadEmails.forEach((e) => { e.isRead = true })
      // Persist to backend
      for (const email of unreadEmails) {
        emailsApi.markAsRead(email.id).catch((err) =>
          console.error('Failed to mark email as read:', err)
        )
      }
    }
    // Bulk-clear the per-client unread email counter on the pipeline card.
    // Fire-and-forget. Skip `email:*` pseudo-IDs used for unknown senders.
    if (thread.clientId && !thread.clientId.startsWith('email:')) {
      wsSend('mark_client_read', { clientId: thread.clientId, channel: 'email' })
    }
  }

  const handleReply = async () => {
    if (!selectedThread || !replySubject.trim() || isQuillEmpty(replyBody)) return

    try {
      setReplySending(true)
      const attachmentIds = replyAttachments.map((a) => a.id)
      await emailsApi.send(
        selectedThread.clientId,
        replySubject.trim(),
        quillHtmlToEmailHtml(replyBody.trim()),
        attachmentIds.length > 0 ? attachmentIds : undefined
      )
      showSuccess('Reply sent')
      setReplyOpen(false)
      setReplySubject('')
      setReplyBody('')
      setReplyAttachments([])
      loadEmails()
    } catch (err) {
      console.error('Error sending reply:', err)
      showError(err instanceof Error ? err.message : 'Failed to send reply')
    } finally {
      setReplySending(false)
    }
  }

  const handleReplyFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files || files.length === 0 || !selectedThread) return

    for (const file of Array.from(files)) {
      try {
        setUploadingReplyAttachment(true)
        const attachment = await attachmentsApi.upload(
          file,
          'client',
          selectedThread.clientId,
          `Email attachment: ${file.name}`,
          false
        )
        setReplyAttachments((prev) => [...prev, attachment])
      } catch (err) {
        console.error('Error uploading attachment:', err)
        showError(`Failed to upload ${file.name}`)
      } finally {
        setUploadingReplyAttachment(false)
      }
    }

    if (replyFileInputRef.current) {
      replyFileInputRef.current.value = ''
    }
  }

  const handleRemoveReplyAttachment = (attachmentId: string) => {
    setReplyAttachments((prev) => prev.filter((a) => a.id !== attachmentId))
  }

  const handleReplyAIGenerate = async () => {
    if (!selectedThread || !replyAiPrompt.trim()) return

    try {
      setReplyGeneratingAI(true)
      const result = await emailsApi.generateEmail(
        selectedThread.clientId || null,
        replyAiPrompt.trim(),
        {
          existingDraft: isQuillEmpty(replyBody) ? undefined : replyBody,
        }
      )
      setReplySubject(result.subject)
      setReplyBody(result.body)
      setReplyAiPrompt('')
      setShowReplyAIInput(false)
      showSuccess(`Reply generated with ${result.tone} tone`)
    } catch (err) {
      console.error('Error generating reply:', err)
      showError(err instanceof Error ? err.message : 'Failed to generate reply')
    } finally {
      setReplyGeneratingAI(false)
    }
  }

  const loadEmails = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await emailsApi.listAll()
      setAllEmails(Array.isArray(data) ? data : [])
    } catch (err) {
      console.error('Error loading emails:', err)
      const errorMessage = err instanceof Error ? err.message : 'Failed to load emails'
      if (errorMessage.includes('Failed to fetch') || errorMessage.includes('NetworkError')) {
        setError('Unable to connect to server. Please check your connection and try again.')
      } else {
        setError(errorMessage)
      }
      setAllEmails([])
    } finally {
      setLoading(false)
    }
  }

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Now'
    if (diffMins < 60) return `${diffMins} min`
    if (diffHours < 24) return `${diffHours} hr`
    if (diffDays < 7) return date.toLocaleDateString([], { weekday: 'short' })
    if (date.getFullYear() === now.getFullYear()) {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' })
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: '2-digit' })
  }

  const formatFullDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString([], {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  }

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  const toggleStar = async (threadId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    const thread = threads.find(t => t.id === threadId)
    if (!thread) return
    const emailIds = thread.emails.map(e => e.id)
    const newStarred = !thread.starred

    // Optimistic update
    setAllEmails(prev => prev.map(e => emailIds.includes(e.id) ? { ...e, isStarred: newStarred } : e))

    try {
      await emailsApi.bulkUpdate(emailIds, { isStarred: newStarred })
    } catch {
      // Revert on failure
      setAllEmails(prev => prev.map(e => emailIds.includes(e.id) ? { ...e, isStarred: !newStarred } : e))
      showError('Failed to update star')
    }
  }

  // Selection handlers
  const toggleThreadSelection = (threadId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    setSelectedThreadIds(prev => {
      const next = new Set(prev)
      next.has(threadId) ? next.delete(threadId) : next.add(threadId)
      return next
    })
  }

  const toggleSelectAll = () => {
    if (selectedThreadIds.size === threads.length) {
      setSelectedThreadIds(new Set())
    } else {
      setSelectedThreadIds(new Set(threads.map(t => t.id)))
    }
  }

  const deselectAll = () => setSelectedThreadIds(new Set())

  const getSelectedEmailIds = () => {
    return threads
      .filter(t => selectedThreadIds.has(t.id))
      .flatMap(t => t.emails.map(e => e.id))
  }

  // Bulk action handlers
  const handleBulkMarkRead = async () => {
    const emailIds = getSelectedEmailIds()
    setAllEmails(prev => prev.map(e => emailIds.includes(e.id) ? { ...e, isRead: true } : e))
    deselectAll()
    try {
      await emailsApi.bulkUpdate(emailIds, { isRead: true })
      showSuccess(`Marked ${selectedThreadIds.size} thread${selectedThreadIds.size > 1 ? 's' : ''} as read`)
    } catch {
      loadEmails()
      showError('Failed to mark as read')
    }
  }

  const handleBulkMarkUnread = async () => {
    const emailIds = getSelectedEmailIds()
    setAllEmails(prev => prev.map(e => emailIds.includes(e.id) ? { ...e, isRead: false } : e))
    deselectAll()
    try {
      await emailsApi.bulkUpdate(emailIds, { isRead: false })
      showSuccess(`Marked ${selectedThreadIds.size} thread${selectedThreadIds.size > 1 ? 's' : ''} as unread`)
    } catch {
      loadEmails()
      showError('Failed to mark as unread')
    }
  }

  const handleBulkArchive = async () => {
    const emailIds = getSelectedEmailIds()
    const count = selectedThreadIds.size
    setAllEmails(prev => prev.map(e => emailIds.includes(e.id) ? { ...e, isArchived: true } : e))
    deselectAll()

    showUndoNotification(`${count} thread${count > 1 ? 's' : ''} archived`, async () => {
      setAllEmails(prev => prev.map(e => emailIds.includes(e.id) ? { ...e, isArchived: false } : e))
      await emailsApi.bulkUpdate(emailIds, { isArchived: false })
    })

    try {
      await emailsApi.bulkUpdate(emailIds, { isArchived: true })
    } catch {
      setAllEmails(prev => prev.map(e => emailIds.includes(e.id) ? { ...e, isArchived: false } : e))
      showError('Failed to archive')
    }
  }

  const handleBulkDelete = async () => {
    setDeleteDialogOpen(false)
    const emailIds = getSelectedEmailIds()
    const count = selectedThreadIds.size
    setAllEmails(prev => prev.map(e => emailIds.includes(e.id) ? { ...e, isDeleted: true } : e))
    deselectAll()

    showUndoNotification(`${count} thread${count > 1 ? 's' : ''} deleted`, async () => {
      setAllEmails(prev => prev.map(e => emailIds.includes(e.id) ? { ...e, isDeleted: false } : e))
      await emailsApi.bulkUpdate(emailIds, { isDeleted: false })
    })

    try {
      await emailsApi.bulkUpdate(emailIds, { isDeleted: true })
    } catch {
      setAllEmails(prev => prev.map(e => emailIds.includes(e.id) ? { ...e, isDeleted: false } : e))
      showError('Failed to delete')
    }
  }

  const toggleEmailExpanded = (emailId: string) => {
    setExpandedEmails((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(emailId)) {
        newSet.delete(emailId)
      } else {
        newSet.add(emailId)
      }
      return newSet
    })
  }

  // Apply active filter client-side
  const filterAppliedEmails = allEmails.filter((email) => {
    switch (activeFilter) {
      case 'unread':
        return !email.isArchived && !email.isDeleted && !email.isRead
      case 'starred':
        return !email.isArchived && !email.isDeleted && email.isStarred
      case 'archived':
        return email.isArchived && !email.isDeleted
      case 'trash':
        return email.isDeleted
      case 'all':
      default:
        return !email.isArchived && !email.isDeleted
    }
  })

  // Filter emails based on search term
  const filteredEmails = filterAppliedEmails.filter((email) => {
    if (!searchTerm) return true
    const term = searchTerm.toLowerCase()
    return (
      email.subject.toLowerCase().includes(term) ||
      email.textBody?.toLowerCase().includes(term) ||
      email.clientName?.toLowerCase().includes(term) ||
      email.email.toLowerCase().includes(term)
    )
  })

  // Build email threads grouped by client (or email address for unknown senders)
  const threadMap = new Map<string, EmailThread>()
  filteredEmails.forEach((email) => {
    // Use clientId if available, otherwise use the email address as thread key
    const threadKey = email.clientId || `email:${email.email}`
    const existing = threadMap.get(threadKey)
    if (!existing) {
      threadMap.set(threadKey, {
        id: threadKey,
        clientId: email.clientId || threadKey,
        clientName: email.clientName || email.email || 'Unknown',
        clientEmail: email.email,
        subject: email.subject,
        lastEmail: email,
        emails: [email],
        emailCount: 1,
        hasUnread: email.direction === 'inbound' && !email.isRead,
        starred: email.isStarred === true,
      })
    } else {
      existing.emails.push(email)
      existing.emailCount++
      if (new Date(email.createdAt) > new Date(existing.lastEmail.createdAt)) {
        existing.lastEmail = email
        existing.subject = email.subject
      }
      if (email.direction === 'inbound' && !email.isRead) {
        existing.hasUnread = true
      }
      if (email.isStarred) {
        existing.starred = true
      }
    }
  })

  // Sort emails within each thread by date
  threadMap.forEach((thread) => {
    thread.emails.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
  })

  const threads = Array.from(threadMap.values()).sort(
    (a, b) => new Date(b.lastEmail.createdAt).getTime() - new Date(a.lastEmail.createdAt).getTime()
  )

  // Update selected thread when emails change
  useEffect(() => {
    if (selectedThread) {
      const updated = threadMap.get(selectedThread.id)
      if (updated) {
        setSelectedThread(updated)
      }
    }
  }, [allEmails])

  // Reset reply state when switching threads
  useEffect(() => {
    setReplyOpen(false)
    setReplyBody('')
    setReplySubject('')
    setReplyAiPrompt('')
    setShowReplyAIInput(false)
    setReplyAttachments([])
  }, [selectedThread?.id])

  const openReply = () => {
    if (selectedThread) {
      const lastSubject = selectedThread.lastEmail.subject
      setReplySubject(lastSubject.startsWith('Re:') ? lastSubject : `Re: ${lastSubject}`)
      setReplyBody('')
      setReplyAiPrompt('')
      setShowReplyAIInput(false)
      setReplyAttachments([])
      setReplyOpen(true)
    }
  }

  if (loading) {
    return <LoadingSpinner />
  }

  // Thread detail view
  if (selectedThread) {
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'grey.50', overflow: 'hidden', maxWidth: '100%' }}>
        {/* Thread Header */}
        <Paper
          sx={{
            p: { xs: 1.5, sm: 2 },
            borderRadius: 0,
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1, sm: 2 },
          }}
          elevation={0}
        >
          <IconButton onClick={() => setSelectedThread(null)} size="large">
            <BackIcon />
          </IconButton>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: { xs: '1rem', sm: '1.25rem' },
              }}
            >
              {selectedThread.subject}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
              }}
            >
              {selectedThread.clientName} &lt;{selectedThread.clientEmail}&gt; - {selectedThread.emailCount} message{selectedThread.emailCount !== 1 ? 's' : ''}
            </Typography>
          </Box>
          {/* An unlinked thread is keyed `email:<address>`, not a client id, so
              this used to navigate to /clients/email:someone@example.com — a
              route that resolves to nothing. Messages guards the same way on
              its `phone:` keys. */}
          {!selectedThread.clientId.startsWith('email:') && (
            <>
              <IconButton
                size="large"
                onClick={() => navigate(`/clients/${selectedThread.clientId}`)}
                title="View Client"
                sx={{ display: { xs: 'inline-flex', sm: 'none' } }}
              >
                <OpenInNewIcon />
              </IconButton>
              <Button
                variant="outlined"
                size="small"
                startIcon={<OpenInNewIcon />}
                onClick={() => navigate(`/clients/${selectedThread.clientId}`)}
                sx={{ whiteSpace: 'nowrap', display: { xs: 'none', sm: 'inline-flex' } }}
              >
                View Client
              </Button>
            </>
          )}
          <IconButton
            size="large"
            onClick={handleExportPdf}
            disabled={exportingPdf}
            title="Download PDF"
            sx={{ display: { xs: 'inline-flex', sm: 'none' } }}
          >
            {exportingPdf ? <CircularProgress size={20} /> : <DownloadIcon />}
          </IconButton>
          <Button
            variant="outlined"
            size="small"
            startIcon={exportingPdf ? <CircularProgress size={16} /> : <DownloadIcon />}
            onClick={handleExportPdf}
            disabled={exportingPdf}
            sx={{ whiteSpace: 'nowrap', display: { xs: 'none', sm: 'inline-flex' } }}
          >
            {exportingPdf ? 'Exporting...' : 'Download PDF'}
          </Button>
        </Paper>

        {/* Email Messages */}
        <Box sx={{ flex: 1, overflow: 'auto', p: { xs: 1.5, sm: 2 } }}>
          {selectedThread.emails.map((email, index) => {
            const isExpanded = expandedEmails.has(email.id) || index === selectedThread.emails.length - 1
            const isOutbound = email.direction === 'outbound'

            return (
              <Paper
                key={email.id}
                sx={{
                  mb: { xs: 1.5, sm: 2 },
                  overflow: 'hidden',
                  border: 1,
                  borderColor: 'divider',
                }}
                elevation={0}
              >
                {/* Email Header - Always visible */}
                <Box
                  sx={{
                    p: { xs: 1.5, sm: 2 },
                    display: 'flex',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    gap: { xs: 1, sm: 2 },
                    cursor: 'pointer',
                    bgcolor: isExpanded ? 'white' : 'grey.50',
                    '&:hover': { bgcolor: isExpanded ? 'white' : 'grey.100' },
                  }}
                  onClick={() => toggleEmailExpanded(email.id)}
                >
                  <Avatar
                    sx={{
                      bgcolor: isOutbound ? colors.primary : 'grey.400',
                      width: { xs: 36, sm: 40 },
                      height: { xs: 36, sm: 40 },
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      flexShrink: 0,
                    }}
                  >
                    {isOutbound ? getInitials(email.authorName || 'Me') : getInitials(selectedThread.clientName)}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', sm: '0.875rem' } }}>
                        {isOutbound ? (email.authorName || 'Me') : selectedThread.clientName}
                      </Typography>
                      {isOutbound && (
                        <Chip
                          label="Sent"
                          size="small"
                          sx={{ height: 18, fontSize: '0.65rem' }}
                          color="primary"
                          variant="outlined"
                        />
                      )}
                      {email.status && statusIcons[email.status]}
                    </Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        display: 'block',
                        fontSize: { xs: '0.7rem', sm: '0.75rem' },
                      }}
                    >
                      {isOutbound
                        ? `To: ${email.toEmails?.join(', ') ?? selectedThread.clientEmail}`
                        : `From: ${selectedThread.clientEmail}`}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: { xs: 'block', sm: 'none' },
                        fontSize: '0.7rem',
                        mt: 0.25,
                      }}
                    >
                      {formatRelativeTime(email.createdAt)}
                    </Typography>
                    {!isExpanded && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mt: 0.5,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                        }}
                      >
                        {emailPreview(email, 100)}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ textAlign: 'right', flexShrink: 0, display: { xs: 'none', sm: 'block' } }}>
                    <Typography variant="caption" color="text.secondary">
                      {formatFullDate(email.createdAt)}
                    </Typography>
                  </Box>
                  <IconButton size="small">
                    {isExpanded ? <CollapseIcon /> : <ExpandIcon />}
                  </IconButton>
                </Box>

                {/* Email Body - Expandable */}
                <Collapse in={isExpanded}>
                  <Divider />
                  <Box sx={{ p: { xs: 1.5, sm: 3 }, overflow: 'hidden' }}>
                    <Box sx={{ maxHeight: 600, overflowY: 'auto' }}>
                    {email.htmlBody ? (
                      <Box
                        sx={{
                          lineHeight: 1.7,
                          color: 'text.primary',
                          overflowX: 'auto',
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                          '& img': { maxWidth: '100%', height: 'auto' },
                          '& table': { maxWidth: '100%', tableLayout: 'fixed' },
                          '& td': { wordBreak: 'break-word' },
                          '& a': { color: 'primary.main', wordBreak: 'break-all' },
                          '& *': { maxWidth: '100%', boxSizing: 'border-box' },
                        }}
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(email.htmlBody, {
                            ADD_TAGS: ['style'],
                            ADD_ATTR: ['target', 'style'],
                          }),
                        }}
                      />
                    ) : (
                      <Typography
                        variant="body1"
                        sx={{
                          whiteSpace: 'pre-wrap',
                          lineHeight: 1.7,
                          color: 'text.primary',
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                        }}
                      >
                        {email.textBody || '(no content)'}
                      </Typography>
                    )}
                    </Box>

                    {/* Attachments */}
                    {email.attachments && email.attachments.length > 0 && (
                      <Box sx={{ mt: { xs: 2, sm: 3 }, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                          Attachments ({email.attachments.length})
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, flexWrap: 'wrap', gap: 1 }}>
                          {email.attachments.map((att) => (
                            <Chip
                              key={att.id}
                              icon={<FileIcon />}
                              label={att.fileName}
                              size="small"
                              onClick={() => handleDownloadAttachment(att.id)}
                              onDelete={() => handleDownloadAttachment(att.id)}
                              deleteIcon={<DownloadIcon />}
                              sx={{
                                cursor: 'pointer',
                                maxWidth: '100%',
                                '& .MuiChip-label': {
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                },
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Collapse>
              </Paper>
            )
          })}

          {/* Reply Box - Inline at end of thread */}
          {replyOpen ? (
            <Paper
              sx={{
                mb: { xs: 1.5, sm: 2 },
                overflow: 'hidden',
                border: 1,
                borderColor: 'primary.main',
                borderRadius: 2,
              }}
              elevation={0}
            >
              <Box
                sx={{
                  p: { xs: 1.25, sm: 1.5 },
                  bgcolor: 'primary.main',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', sm: '0.875rem' } }}>
                  Reply to {selectedThread.clientName}
                </Typography>
                <IconButton size="small" onClick={() => setReplyOpen(false)} sx={{ color: 'white' }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
              <Box sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: 'white' }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Subject"
                  value={replySubject}
                  onChange={(e) => setReplySubject(e.target.value)}
                  sx={{
                    mb: 2,
                    '& .MuiInputBase-input': { fontSize: { xs: '0.875rem', sm: '1rem' } },
                    '& .MuiInputLabel-root': { fontSize: { xs: '0.875rem', sm: '1rem' } },
                  }}
                />
                <Box sx={{
                  mb: 2,
                  '& .ql-container': { border: 'none', fontSize: { xs: '0.875rem', sm: '1rem' } },
                  '& .ql-toolbar': { border: 'none', borderBottom: 1, borderColor: 'divider' },
                  '& .ql-editor': { minHeight: 150, lineHeight: 1.6 },
                  '& .ql-editor.ql-blank::before': { fontStyle: 'normal', color: 'text.disabled' },
                  '& .ql-editor table': { borderCollapse: 'collapse', width: '100%', mb: '1em' },
                  '& .ql-editor th, & .ql-editor td': { border: '1px solid #ddd', padding: '8px', textAlign: 'left' },
                  '& .ql-editor th': { backgroundColor: '#f5f5f5', fontWeight: 600 },
                  border: 1, borderColor: 'divider', borderRadius: 1,
                }}>
                  <ReactQuill
                    theme="snow"
                    value={replyBody}
                    onChange={setReplyBody}
                    modules={quillModules}
                    placeholder="Write your reply..."
                  />
                </Box>

                {/* AI Assist Input */}
                <Collapse in={showReplyAIInput}>
                  <Box sx={{ mb: 2, p: { xs: 1.25, sm: 1.5 }, bgcolor: 'primary.50', borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', flexDirection: { xs: 'column', sm: 'row' } }}>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', flex: 1, minWidth: 0, width: { xs: '100%', sm: 'auto' } }}>
                        <AIIcon color="primary" sx={{ mb: 0.5, display: { xs: 'none', sm: 'block' } }} />
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Describe what you want to write..."
                          value={replyAiPrompt}
                          onChange={(e) => setReplyAiPrompt(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault()
                              handleReplyAIGenerate()
                            }
                            if (e.key === 'Escape') {
                              setShowReplyAIInput(false)
                              setReplyAiPrompt('')
                            }
                          }}
                          disabled={replyGeneratingAI}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              bgcolor: 'white',
                              borderRadius: 2,
                              fontSize: { xs: '0.875rem', sm: '1rem' },
                            },
                          }}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', width: { xs: '100%', sm: 'auto' } }}>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={handleReplyAIGenerate}
                          disabled={!replyAiPrompt.trim() || replyGeneratingAI}
                          sx={{ minWidth: 80, height: 40, flex: { xs: 1, sm: 'initial' } }}
                        >
                          {replyGeneratingAI ? <CircularProgress size={20} color="inherit" /> : 'Generate'}
                        </Button>
                        <IconButton size="small" onClick={() => { setShowReplyAIInput(false); setReplyAiPrompt('') }}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ ml: { xs: 0, sm: 4 }, mt: 0.5, display: 'block', fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                    >
                      {!isQuillEmpty(replyBody) ? 'Polish your draft' : 'Write a reply'}
                    </Typography>
                  </Box>
                </Collapse>

                {/* Reply Attachments */}
                {replyAttachments.length > 0 && (
                  <Box sx={{ mb: 2, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, flexWrap: 'wrap', gap: 0.5 }}>
                    {replyAttachments.map((att) => (
                      <Chip
                        key={att.id}
                        icon={<FileIcon />}
                        label={`${att.fileName} (${formatFileSize(att.fileSize)})`}
                        size="small"
                        onDelete={() => handleRemoveReplyAttachment(att.id)}
                        deleteIcon={<CloseIcon />}
                        sx={{
                          maxWidth: '100%',
                          '& .MuiChip-label': {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          },
                        }}
                      />
                    ))}
                  </Box>
                )}

                {uploadingReplyAttachment && <LinearProgress sx={{ mb: 2 }} />}

                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexDirection: { xs: 'column-reverse', sm: 'row' },
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 0.5, width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'flex-start', sm: 'flex-start' } }}>
                    <input
                      type="file"
                      ref={replyFileInputRef}
                      onChange={handleReplyFileSelect}
                      multiple
                      style={{ display: 'none' }}
                    />
                    <Tooltip title="AI Assist">
                      <IconButton
                        onClick={() => setShowReplyAIInput(!showReplyAIInput)}
                        color={showReplyAIInput ? 'primary' : 'default'}
                        size="large"
                      >
                        <AIIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Attach files">
                      <IconButton
                        onClick={() => replyFileInputRef.current?.click()}
                        disabled={uploadingReplyAttachment}
                        size="large"
                      >
                        <AttachFileIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
                    <Button
                      onClick={() => { setReplyOpen(false); setReplyAttachments([]); setShowReplyAIInput(false); setReplyAiPrompt('') }}
                      sx={{ flex: { xs: 1, sm: 'initial' }, fontSize: { xs: '0.875rem', sm: '0.875rem' } }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleReply}
                      disabled={replySending || !replySubject.trim() || isQuillEmpty(replyBody)}
                      startIcon={replySending ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
                      sx={{ flex: { xs: 1, sm: 'initial' }, fontSize: { xs: '0.875rem', sm: '0.875rem' } }}
                    >
                      {replySending ? 'Sending...' : 'Send'}
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Paper>
          ) : (
            <Paper
              sx={{
                mb: { xs: 1.5, sm: 2 },
                p: { xs: 1.5, sm: 2 },
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
                cursor: 'pointer',
                '&:hover': { borderColor: 'primary.main', bgcolor: 'grey.50' },
              }}
              elevation={0}
              onClick={openReply}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 } }}>
                <Avatar sx={{ bgcolor: 'grey.300', width: { xs: 36, sm: 40 }, height: { xs: 36, sm: 40 } }}>
                  <ReplyIcon sx={{ color: 'grey.600', fontSize: { xs: 20, sm: 24 } }} />
                </Avatar>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '0.875rem' } }}>
                  Click to reply to {selectedThread.clientName}...
                </Typography>
              </Box>
            </Paper>
          )}

          <div ref={emailsEndRef} />
        </Box>

        {/* Compose Drawer */}
        {composeOpen && renderComposeDrawer()}
      </Box>
    )
  }

  // Helper function to render compose drawer
  function renderComposeDrawer() {
    return (
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          bottom: { xs: composeMinimized ? 'auto' : 0, sm: 0 },
          top: { xs: composeMinimized ? 'auto' : 0, sm: 'auto' },
          right: { xs: 0, sm: 80 },
          left: { xs: 0, sm: 'auto' },
          width: { xs: '100%', sm: composeMinimized ? 300 : 500 },
          height: { xs: composeMinimized ? 'auto' : '100%', sm: 'auto' },
          maxHeight: { xs: composeMinimized ? '48px' : '100%', sm: composeMinimized ? 48 : 'calc(100vh - 100px)' },
          borderRadius: { xs: 0, sm: '12px 12px 0 0' },
          overflow: 'hidden',
          zIndex: 1300,
          display: 'flex',
          flexDirection: 'column',
          transition: 'width 0.2s ease, max-height 0.2s ease',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1,
            bgcolor: 'primary.main',
            color: 'white',
            cursor: 'pointer',
          }}
          onClick={() => composeMinimized && setComposeMinimized(false)}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {composeMinimized ? composeSubject || 'New Message' : 'New Message'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation()
                setComposeMinimized(!composeMinimized)
              }}
              sx={{ color: 'white' }}
            >
              {composeMinimized ? <MaximizeIcon fontSize="small" /> : <MinimizeIcon fontSize="small" />}
            </IconButton>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation()
                handleCloseCompose()
              }}
              sx={{ color: 'white' }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        <Collapse in={!composeMinimized}>
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', height: { xs: 'calc(100vh - 48px)', sm: 'auto' } }}>
            {/* Toggle between client select and manual entry */}
            <Box sx={{ display: 'flex', gap: 1, px: { xs: 1.5, sm: 2 }, pt: 1.5 }}>
              <Button
                variant={manualEmailMode ? 'outlined' : 'contained'}
                size="small"
                onClick={() => {
                  setManualEmailMode(false)
                  setManualEmail('')
                }}
                sx={{ flex: 1, fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
              >
                Select Client
              </Button>
              <Button
                variant={manualEmailMode ? 'contained' : 'outlined'}
                size="small"
                onClick={() => {
                  setManualEmailMode(true)
                  setComposeClient(null)
                }}
                sx={{ flex: 1, fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}
              >
                Enter Email
              </Button>
            </Box>

            <Box sx={{ px: { xs: 1.5, sm: 2 }, pt: 1.5, borderBottom: 1, borderColor: 'divider' }}>
              {manualEmailMode ? (
                <TextField
                  fullWidth
                  placeholder="To: a@example.com, b@example.com"
                  inputProps={{ 'aria-label': 'To' }}
                  value={manualEmail}
                  onChange={(e) => setManualEmail(e.target.value)}
                  variant="standard"
                  InputProps={{ disableUnderline: true }}
                  sx={{
                    '& .MuiInput-root': { py: 0.5, fontSize: { xs: '0.875rem', sm: '1rem' } },
                  }}
                />
              ) : (
                <ClientPicker
                  options={composeClientOptions}
                  loading={loadingClients}
                  value={composeClient}
                  onChange={setComposeClient}
                  isManager={isManager}
                  assignedClientIds={assignedClientIds}
                  secondary={(c) => c.email}
                  label={null}
                  placeholder="To"
                  inputVariant="standard"
                  sx={{
                    '& .MuiInput-root': { py: 0.5, fontSize: { xs: '0.875rem', sm: '1rem' } },
                  }}
                />
              )}
            </Box>

            <Box sx={{ px: { xs: 1.5, sm: 2 }, borderBottom: 1, borderColor: 'divider' }}>
              <TextField
                fullWidth
                placeholder="Subject"
                value={composeSubject}
                onChange={(e) => setComposeSubject(e.target.value)}
                variant="standard"
                InputProps={{ disableUnderline: true }}
                sx={{
                  '& .MuiInput-root': { py: 1, fontSize: { xs: '0.875rem', sm: '1rem' } },
                }}
              />
            </Box>

            <Box sx={{
              flex: 1, overflow: 'auto', px: { xs: 1.5, sm: 2 }, py: 1,
              '& .ql-container': { border: 'none', fontSize: { xs: '0.875rem', sm: '1rem' } },
              '& .ql-toolbar': { border: 'none', borderBottom: 1, borderColor: 'divider' },
              '& .ql-editor': { minHeight: 200, maxHeight: 400, overflow: 'auto', lineHeight: 1.6 },
              '& .ql-editor.ql-blank::before': { fontStyle: 'normal', color: 'text.disabled' },
              '& .ql-editor table': { borderCollapse: 'collapse', width: '100%', mb: '1em' },
              '& .ql-editor th, & .ql-editor td': { border: '1px solid #ddd', padding: '8px', textAlign: 'left' },
              '& .ql-editor th': { backgroundColor: '#f5f5f5', fontWeight: 600 },
            }}>
              <ReactQuill
                theme="snow"
                value={composeBody}
                onChange={setComposeBody}
                modules={quillModules}
                placeholder="Compose email..."
              />
            </Box>

            <Collapse in={showAIInput}>
              <Box sx={{ px: { xs: 1.5, sm: 2 }, py: 1.5, bgcolor: 'primary.50', borderTop: 1, borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', flexDirection: { xs: 'column', sm: 'row' } }}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', width: { xs: '100%', sm: 'auto' }, flex: { sm: 1 } }}>
                    <AIIcon color="primary" sx={{ mb: 0.5, display: { xs: 'none', sm: 'block' } }} />
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Describe what you want to write..."
                      value={aiPrompt}
                      onChange={(e) => setAiPrompt(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault()
                          handleAIGenerate()
                        }
                        if (e.key === 'Escape') {
                          setShowAIInput(false)
                          setAiPrompt('')
                        }
                      }}
                      disabled={generatingAI}
                      sx={{
                        '& .MuiOutlinedInput-root': { bgcolor: 'white', borderRadius: 2, fontSize: { xs: '0.875rem', sm: '1rem' } },
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5, width: { xs: '100%', sm: 'auto' } }}>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleAIGenerate}
                      disabled={!aiPrompt.trim() || generatingAI}
                      sx={{ minWidth: 80, height: 40, flex: { xs: 1, sm: 'initial' } }}
                    >
                      {generatingAI ? <CircularProgress size={20} color="inherit" /> : 'Generate'}
                    </Button>
                    <IconButton size="small" onClick={() => { setShowAIInput(false); setAiPrompt('') }}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ ml: { xs: 0, sm: 4 }, mt: 0.5, display: 'block', fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                >
                  {!isQuillEmpty(composeBody) ? 'Polish your draft' : 'Compose a new email'}
                </Typography>
              </Box>
            </Collapse>

            {composeAttachments.length > 0 && (
              <Box sx={{ px: { xs: 1.5, sm: 2 }, py: 1, borderTop: 1, borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, flexWrap: 'wrap', gap: 0.5 }}>
                  {composeAttachments.map((att) => (
                    <Chip
                      key={att.id}
                      icon={<FileIcon />}
                      label={`${att.fileName} (${formatFileSize(att.fileSize)})`}
                      size="small"
                      onDelete={() => handleRemoveAttachment(att.id)}
                      deleteIcon={<CloseIcon />}
                      sx={{
                        maxWidth: '100%',
                        '& .MuiChip-label': {
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        },
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {uploadingAttachment && <LinearProgress />}

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: { xs: 1.5, sm: 2 },
                py: 1.5,
                borderTop: 1,
                borderColor: 'divider',
                bgcolor: 'grey.50',
                gap: 1,
              }}
            >
              <Button
                variant="contained"
                onClick={handleComposeSend}
                disabled={composeSending || (manualEmailMode ? !manualEmail.trim() : !composeClient) || !composeSubject.trim() || isQuillEmpty(composeBody)}
                startIcon={composeSending ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
                sx={{
                  borderRadius: 2,
                  fontSize: { xs: '0.875rem', sm: '0.875rem' },
                  px: { xs: 2, sm: 3 },
                }}
              >
                {composeSending ? 'Sending' : 'Send'}
              </Button>

              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Tooltip title="AI Assist">
                  <IconButton
                    onClick={() => setShowAIInput(!showAIInput)}
                    color={showAIInput ? 'primary' : 'default'}
                    size="large"
                  >
                    <AIIcon />
                  </IconButton>
                </Tooltip>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  multiple
                  style={{ display: 'none' }}
                />
                <Tooltip title="Attach files">
                  <IconButton
                    onClick={() => fileInputRef.current?.click()}
                    disabled={!composeClient || uploadingAttachment}
                    size="large"
                  >
                    <AttachFileIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Box>
        </Collapse>
      </Paper>
    )
  }

  // Main inbox view
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', maxWidth: '100%' }}>
      {/* Toolbar */}
      <Paper
        sx={{
          p: { xs: 1.5, sm: 2 },
          borderRadius: 0,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 1, sm: 2 },
          flexWrap: 'wrap',
        }}
        elevation={0}
      >
        <Typography variant="h5" sx={{ fontWeight: 600, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          Inbox
        </Typography>
        <PageHelpButton articleId="admin-emails-manage-inbox" />
        {/* MWA-335 — arriving from a client record filtered the inbox but left
            no way back to it until you happened to open a thread. */}
        {filterClientId && (
          <Button
            size="small"
            startIcon={<OpenInNewIcon />}
            onClick={() => navigate(`/clients/${filterClientId}`)}
            sx={{ whiteSpace: 'nowrap' }}
          >
            View Client
          </Button>
        )}
        <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 'auto' }, order: { xs: 3, sm: 0 } }}>
          <TextField
            placeholder="Search emails..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              maxWidth: { sm: 400 },
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                backgroundColor: 'grey.100',
                '& fieldset': { border: 'none' },
              },
            }}
          />
        </Box>
        <IconButton onClick={loadEmails} title="Refresh" size="large">
          <RefreshIcon />
        </IconButton>
        <Button
          variant="contained"
          startIcon={<AddIcon sx={{ display: { xs: 'none', sm: 'inline-block' } }} />}
          onClick={handleOpenCompose}
          sx={{ borderRadius: 2, minWidth: { xs: 'auto', sm: 'auto' }, px: { xs: 2, sm: 3 } }}
        >
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Compose</Box>
          <AddIcon sx={{ display: { xs: 'inline-block', sm: 'none' } }} />
        </Button>
      </Paper>

      {/* Filter Tabs */}
      <Box sx={{ display: 'flex', gap: 1, px: 2, py: 1, borderBottom: 1, borderColor: 'divider', flexWrap: 'wrap' }}>
        {([
          { key: 'all' as const, label: 'All', icon: <InboxIcon sx={{ fontSize: 16 }} /> },
          { key: 'unread' as const, label: 'Unread', icon: <MarkUnreadIcon sx={{ fontSize: 16 }} /> },
          { key: 'starred' as const, label: 'Starred', icon: <StarIcon sx={{ fontSize: 16 }} /> },
          { key: 'archived' as const, label: 'Archived', icon: <ArchiveIcon sx={{ fontSize: 16 }} /> },
          { key: 'trash' as const, label: 'Trash', icon: <TrashIcon sx={{ fontSize: 16 }} /> },
        ]).map(({ key, label, icon }) => (
          <Chip
            key={key}
            icon={icon}
            label={label}
            size="small"
            variant={activeFilter === key ? 'filled' : 'outlined'}
            color={activeFilter === key ? 'primary' : 'default'}
            onClick={() => { setActiveFilter(key); deselectAll() }}
          />
        ))}
      </Box>

      {/* Bulk Actions Toolbar */}
      {selectedThreadIds.size > 0 && (
        <Paper
          sx={{
            px: 2,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'primary.50',
          }}
          elevation={0}
        >
          <Checkbox
            size="small"
            checked={selectedThreadIds.size === threads.length}
            indeterminate={selectedThreadIds.size > 0 && selectedThreadIds.size < threads.length}
            onChange={toggleSelectAll}
          />
          <Typography variant="body2" sx={{ fontWeight: 600, mr: 1 }}>
            {selectedThreadIds.size} selected
          </Typography>
          <Tooltip title="Mark as read">
            <IconButton size="small" onClick={handleBulkMarkRead}>
              <MarkReadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Mark as unread">
            <IconButton size="small" onClick={handleBulkMarkUnread}>
              <MarkUnreadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Archive">
            <IconButton size="small" onClick={handleBulkArchive}>
              <ArchiveIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" color="error" onClick={() => setDeleteDialogOpen(true)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Box sx={{ flex: 1 }} />
          <Button size="small" onClick={deselectAll}>Deselect</Button>
        </Paper>
      )}

      {error && (
        <Alert severity="error" sx={{ m: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Email List */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {threads.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: 'text.secondary',
            }}
          >
            <EmailIcon sx={{ fontSize: 80, mb: 2, opacity: 0.3 }} />
            <Typography variant="h6" sx={{ mb: 1 }}>
              {searchTerm ? 'No emails match your search' : activeFilter !== 'all' ? `No ${activeFilter} emails` : 'Your inbox is empty'}
            </Typography>
            <Typography variant="body2" sx={{ mb: 3 }}>
              {searchTerm ? 'Try a different search term' : activeFilter !== 'all' ? 'Try a different filter' : 'Start a conversation with a client'}
            </Typography>
            {!searchTerm && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCompose}>
                Compose Email
              </Button>
            )}
          </Box>
        ) : (
          threads.map((thread, index) => (
            <Box key={thread.id}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  px: { xs: 1.5, sm: 2 },
                  py: { xs: 1.5, sm: 1.5 },
                  cursor: 'pointer',
                  bgcolor: thread.hasUnread ? 'primary.50' : 'white',
                  '&:hover': { bgcolor: 'grey.100' },
                  borderLeft: thread.hasUnread ? 4 : 0,
                  borderColor: 'primary.main',
                  gap: { xs: 1.5, sm: 0 },
                }}
                onClick={() => handleSelectThread(thread)}
              >
                <Checkbox
                  size="small"
                  checked={selectedThreadIds.has(thread.id)}
                  onClick={(e) => toggleThreadSelection(thread.id, e)}
                  sx={{ mr: 1, display: { xs: 'none', sm: 'inline-flex' } }}
                />
                <IconButton
                  size="small"
                  onClick={(e) => toggleStar(thread.id, e)}
                  sx={{ mr: 1, display: { xs: 'none', sm: 'inline-flex' } }}
                >
                  {thread.starred ? (
                    <StarIcon sx={{ color: 'warning.main' }} />
                  ) : (
                    <StarBorderIcon sx={{ color: 'text.secondary' }} />
                  )}
                </IconButton>
                <Avatar
                  sx={{
                    bgcolor: colors.primary,
                    width: { xs: 40, sm: 36 },
                    height: { xs: 40, sm: 36 },
                    mr: { xs: 0, sm: 2 },
                    fontSize: '0.875rem',
                    flexShrink: 0,
                  }}
                >
                  {getInitials(thread.clientName)}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: thread.hasUnread ? 700 : 600,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontSize: { xs: '0.875rem', sm: '0.875rem' },
                        }}
                      >
                        {thread.clientName}
                      </Typography>
                      {thread.emailCount > 1 && (
                        <Chip
                          label={thread.emailCount}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: '0.65rem',
                            bgcolor: 'grey.200',
                            flexShrink: 0,
                          }}
                        />
                      )}
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: thread.hasUnread ? 'primary.main' : 'text.secondary',
                        fontWeight: thread.hasUnread ? 600 : 400,
                        whiteSpace: 'nowrap',
                        fontSize: { xs: '0.7rem', sm: '0.75rem' },
                        flexShrink: 0,
                      }}
                    >
                      {formatRelativeTime(thread.lastEmail.createdAt)}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: thread.hasUnread ? 600 : 400,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                    }}
                  >
                    {thread.subject}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        fontWeight: 400,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      {emailPreview(thread.lastEmail, 60)}
                    </Typography>
                    {thread.lastEmail.attachments && thread.lastEmail.attachments.length > 0 && (
                      <AttachFileIcon sx={{ color: 'text.secondary', fontSize: 16, flexShrink: 0 }} />
                    )}
                    {getClientSuccessName(thread.clientId) && (
                      <Chip
                        label={`CS: ${getClientSuccessName(thread.clientId)}`}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: '0.625rem',
                          bgcolor: 'success.light',
                          color: 'success.dark',
                          fontWeight: 500,
                          display: { xs: 'none', sm: 'inline-flex' },
                          flexShrink: 0,
                        }}
                      />
                    )}
                  </Box>
                </Box>
              </Box>
              {index < threads.length - 1 && <Divider />}
            </Box>
          ))
        )}
      </Box>

      {/* Compose Drawer */}
      {composeOpen && renderComposeDrawer()}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete threads?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will delete {selectedThreadIds.size} thread{selectedThreadIds.size > 1 ? 's' : ''}. You can undo this within 5 seconds.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleBulkDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
